import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TrendingUp, Package, DollarSign, Calendar, CheckCircle, Clock,
  AlertCircle, Trash2, Check, ChevronDown, ChevronUp, CreditCard,
  ExternalLink, Info, X, ArrowRight, FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import financeService from '../services/financeService';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import ProjectInitialBalances from './ProjectInitialBalances';

// Helper functions for date filters
const getMonthName = (month) => {
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return months[month - 1] || '';
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i);
  }
  return years;
};

const formatLocalDate = (dateStr) => {
  if (!dateStr) return '-';
  if (typeof dateStr === 'string') {
    const pureDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const parts = pureDate.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  const d = new Date(dateStr);
  return isNaN(d) ? '-' : d.toLocaleDateString('pt-BR');
};

const DateFilter = ({ filters, setFilters }) => {
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const handlePreviousMonth = () => {
    let newMonth = filters.month ? parseInt(filters.month) : currentMonth;
    let newYear = filters.year ? parseInt(filters.year) : currentYear;

    if (newMonth === 1) {
      newMonth = 12;
      newYear -= 1;
    } else {
      newMonth -= 1;
    }
    setFilters({ month: newMonth.toString(), year: newYear.toString() });
  };

  const handleNextMonth = () => {
    let newMonth = filters.month ? parseInt(filters.month) : currentMonth;
    let newYear = filters.year ? parseInt(filters.year) : currentYear;

    if (newMonth === 12) {
      newMonth = 1;
      newYear += 1;
    } else {
      newMonth += 1;
    }
    setFilters({ month: newMonth.toString(), year: newYear.toString() });
  };

  const handleCurrentMonth = () => {
    setFilters({ month: currentMonth.toString(), year: currentYear.toString() });
  };

  const displayMonth = filters.month ? parseInt(filters.month) : currentMonth;
  const displayYear = filters.year ? parseInt(filters.year) : currentYear;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Navegação */}
        <button
          onClick={handlePreviousMonth}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
          title="Mês Anterior"
        >
          ‹
        </button>

        {/* Select Mês */}
        <select
          value={filters.month || currentMonth}
          onChange={(e) => setFilters({ ...filters, month: e.target.value })}
          className="px-3 py-2 border rounded-lg bg-white text-gray-700 font-medium min-w-[130px]"
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>{getMonthName(m)}</option>
          ))}
        </select>

        {/* Select Ano */}
        <select
          value={filters.year || currentYear}
          onChange={(e) => setFilters({ ...filters, year: e.target.value })}
          className="px-3 py-2 border rounded-lg bg-white text-gray-700 font-medium min-w-[100px]"
        >
          {getYearOptions().map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <button
          onClick={handleNextMonth}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
          title="Próximo Mês"
        >
          ›
        </button>

        <button
          onClick={handleCurrentMonth}
          className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg font-medium transition-colors text-sm"
        >
          Mês Atual
        </button>

        {/* Separador */}
        <div className="w-px h-8 bg-gray-300 mx-2" />

        {/* Filtro de Intervalo de Datas */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">De:</span>
          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            className="px-2 py-1.5 border rounded-lg text-gray-700 text-sm"
          />
          <span className="text-sm text-gray-600">Até:</span>
          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            className="px-2 py-1.5 border rounded-lg text-gray-700 text-sm"
          />
          {(filters.dateFrom || filters.dateTo) && (
            <button
              onClick={() => setFilters({ ...filters, dateFrom: '', dateTo: '' })}
              className="px-2 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm"
              title="Limpar filtro de datas"
            >
              ✕
            </button>
          )}
        </div>

        {/* Período exibido */}
        <span className="ml-auto text-sm text-gray-600 font-medium">
          {filters.dateFrom || filters.dateTo
            ? `${filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString('pt-BR') : '...'} - ${filters.dateTo ? new Date(filters.dateTo).toLocaleDateString('pt-BR') : '...'}`
            : `Período: ${getMonthName(displayMonth)} / ${displayYear}`
          }
        </span>
      </div>
    </div>
  );
};

const FinanceForecastedIncomes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canView = hasPermission(user?.role, 'financial:view') || hasPermission(user?.role, 'financial:full') || hasPermission(user?.role, 'financial:expenses');
  const canDelete = hasPermission(user?.role, 'financial:full');

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
  const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;

  const [filters, setFilters] = useState({ month: nextMonth.toString(), year: nextYear.toString() });
  const [showOnlyInitialBalances, setShowOnlyInitialBalances] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Modal de recebimento/baixa rápida
  const [receiveModal, setReceiveModal] = useState({
    open: false,
    item: null,
    receivedAmount: '',
    paymentDate: '',
    paymentMethod: 'DINHEIRO',
    observations: ''
  });

  // Modal de saldo inicial integrado
  const [initialBalanceModal, setInitialBalanceModal] = useState({
    open: false,
    projectId: null,
    projectName: '',
    clientName: ''
  });

  // Controle de expansão de recibos individuais da parcela
  const [expandedReceipts, setExpandedReceipts] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ['finance-forecasted-incomes', filters],
    queryFn: () => {
      const params = {};
      if (filters.month && filters.month !== '') params.month = filters.month;
      if (filters.year && filters.year !== '') params.year = filters.year;
      if (filters.dateFrom && filters.dateFrom !== '') params.dateFrom = filters.dateFrom;
      if (filters.dateTo && filters.dateTo !== '') params.dateTo = filters.dateTo;
      return financeService.getForecastedIncomes(params);
    },
    staleTime: 0,
    refetchOnWindowFocus: true
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['finance-forecasted-incomes'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.month, filters.year, filters.dateFrom, filters.dateTo]);

  const deleteMutation = useMutation({
    mutationFn: (id) => financeService.deleteExpectedIncome(id),
    onSuccess: () => {
      toast.success('Entrada prevista excluída');
      queryClient.invalidateQueries({ queryKey: ['finance-forecasted-incomes'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Erro ao excluir');
    }
  });

  const receiveMutation = useMutation({
    mutationFn: ({ id, payload }) => financeService.receiveForecastedIncome(id, payload),
    onSuccess: (res) => {
      toast.success(res?.message || 'Recebimento registrado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['finance-forecasted-incomes'] });
      queryClient.invalidateQueries({ queryKey: ['finance-stats'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox'] });
      setReceiveModal({ open: false, item: null, receivedAmount: '', paymentDate: '', paymentMethod: 'DINHEIRO', observations: '' });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Erro ao registrar recebimento');
    }
  });

  const handleDelete = (id) => {
    setPendingDeleteId(id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteId) {
      deleteMutation.mutate(pendingDeleteId);
    }
    setShowConfirmModal(false);
    setPendingDeleteId(null);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setPendingDeleteId(null);
  };

  const handleOpenReceive = (project) => {
    const pendingVal = parseFloat(project.pending_amount !== undefined ? project.pending_amount : project.expected_amount || 0);
    const today = new Date().toISOString().split('T')[0];
    setReceiveModal({
      open: true,
      item: project,
      receivedAmount: pendingVal > 0 ? pendingVal.toFixed(2).replace('.', ',') : '',
      paymentDate: today,
      paymentMethod: (project.payment_method || 'DINHEIRO').toUpperCase(),
      observations: ''
    });
  };

  const handleConfirmReceive = (e) => {
    e.preventDefault();
    if (!receiveModal.item) return;

    let cleanVal = String(receiveModal.receivedAmount).trim().replace(/\./g, '').replace(',', '.');
    const parsedAmount = parseFloat(cleanVal);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error('Informe um valor de recebimento válido maior que zero');
      return;
    }

    receiveMutation.mutate({
      id: receiveModal.item.id,
      payload: {
        receivedAmount: parsedAmount,
        paymentDate: receiveModal.paymentDate,
        paymentMethod: receiveModal.paymentMethod,
        observations: receiveModal.observations
      }
    });
  };

  const toggleExpandReceipts = (id) => {
    setExpandedReceipts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenInitialBalance = (project) => {
    setInitialBalanceModal({
      open: true,
      projectId: project.project_id,
      projectName: project.project_name || project.project_code,
      clientName: project.client_name || ''
    });
  };

  const forecastData = data?.data || {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    projects: [],
    totals: {
      total: 0,
      pending: 0,
      partial: 0,
      completed: 0
    }
  };

  // Filtragem dinâmica de projetos
  const displayedProjects = useMemo(() => {
    return (forecastData.projects || []).filter(p => !showOnlyInitialBalances || p.initial_balance_id);
  }, [forecastData.projects, showOnlyInitialBalances]);

  // Recálculo dinâmico calibrado dos 4 cards de KPI conforme os filtros ativos
  const displayedTotals = useMemo(() => {
    return displayedProjects.reduce((acc, p) => {
      const exp = parseFloat(p.expected_amount || 0);
      const rec = parseFloat(p.total_received || 0);
      const pend = parseFloat(p.pending_amount || 0);
      acc.total += exp;
      acc.pending += pend;
      acc.completed += rec;
      if (p.status === 'PARTIAL' || (rec > 0 && pend > 0)) {
        acc.partial += pend;
      }
      return acc;
    }, { total: 0, pending: 0, partial: 0, completed: 0 });
  }, [displayedProjects]);

  const getPaymentMethodBadge = (method) => {
    const m = (method || '').toUpperCase();
    if (m.includes('PIX')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">PIX</span>;
    }
    if (m.includes('CART') || m.includes('CARD')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200">CARTÃO</span>;
    }
    if (m.includes('CHEQUE')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">CHEQUE</span>;
    }
    if (m.includes('DINHEIRO') || m.includes('CASH')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800 border border-green-200">DINHEIRO</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200">{method || '-'}</span>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">Pendente</span>;
      case 'PARTIAL':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">Parcial</span>;
      case 'COMPLETED':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">Completo</span>;
      case 'RECEIVED':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-emerald-100 text-emerald-800">Recebido</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getProjectStatusBadge = (status) => {
    const statusMap = {
      'PLANNING': { label: 'Planejamento', color: 'bg-blue-100 text-blue-800' },
      'IN_PROGRESS': { label: 'Em Andamento', color: 'bg-yellow-100 text-yellow-800' },
      'PRODUCTION': { label: 'Produção', color: 'bg-purple-100 text-purple-800' },
      'ASSEMBLY': { label: 'Montagem', color: 'bg-indigo-100 text-indigo-800' }
    };
    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 text-xs font-semibold rounded ${statusInfo.color}`}>{statusInfo.label}</span>;
  };

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Você não tem permissão para visualizar o módulo financeiro.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Entradas Previstas</h1>
          <p className="text-gray-600">Visão detalhada das entradas previstas de projetos para o período</p>
        </div>
        <div className="flex gap-2">
          <Link to="/financeiro" className="px-3 py-2 border rounded-lg">Dashboard</Link>
          <Link to="/financeiro/entradas" className="px-3 py-2 border rounded-lg">Entradas</Link>
          <Link to="/financeiro/saidas" className="px-3 py-2 border rounded-lg">Saídas</Link>
          <Link to="/financeiro/folha" className="px-3 py-2 border rounded-lg">Folha</Link>
        </div>
      </div>

      {/* Filtros */}
      <DateFilter filters={filters} setFilters={setFilters} />

      {/* Filtro Saldos Iniciais */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showOnlyInitialBalances}
            onChange={(e) => setShowOnlyInitialBalances(e.target.checked)}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Mostrar apenas Saldos Iniciais (proj. antigos)
          </span>
        </label>
        <span className="text-xs text-gray-500">
          ({forecastData.projects.filter(p => p.initial_balance_id).length} registros)
        </span>
      </div>

      {/* Cards de totais */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-500">Total Previsto</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(displayedTotals.total)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-500">Pendente</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(displayedTotals.pending)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-500">Parcial</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(displayedTotals.partial)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg shadow border-2 border-green-200">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-600 font-medium">Completo</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(displayedTotals.completed)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Entradas Previstas */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <Package className="h-5 w-5" />
            Entradas Previstas de Projetos
          </h2>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-gray-600">Carregando...</div>
        ) : forecastData.projects.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>Nenhuma entrada prevista encontrada para este período.</p>
            <p className="text-sm mt-2">As entradas previstas aparecem aqui quando um orçamento de projeto é aprovado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Projeto / Obra</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status Projeto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forma Pagto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Previsto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recebido</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pendente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Prevista</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedProjects.map((project) => {
                  const isPaid = project.status === 'RECEIVED' || project.status === 'COMPLETED';
                  const isLate = project.expected_date && new Date(project.expected_date) < new Date(new Date().setHours(0, 0, 0, 0)) && !isPaid && project.pending_amount > 0;
                  const contract = project.contract;
                  const contractPercent = contract && contract.total > 0
                    ? Math.min(100, Math.round(((contract.total_received || 0) / contract.total) * 100))
                    : 0;
                  const hasReceipts = project.receipts && project.receipts.length > 0;
                  const isExpanded = !!expandedReceipts[project.id];

                  return (
                    <React.Fragment key={project.id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          {project.initial_balance_id && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 mb-1">
                              Recebidos (proj. antigos)
                            </span>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-900">{project.project_code}</span>
                            {project.parcel_label && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                {project.parcel_label}
                              </span>
                            )}
                          </div>
                          {project.project_name && (
                            <div className="text-xs text-gray-600 mt-0.5">{project.project_name}</div>
                          )}
                          {contract && contract.total > 0 && (
                            <div className="mt-1 text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-100 flex flex-wrap gap-x-2 gap-y-0.5">
                              <span>Contrato: <strong className="text-gray-700">{formatCurrency(contract.total)}</strong></span>
                              <span>• Pago: <strong className="text-emerald-700">{formatCurrency(contract.total_received)}</strong> ({contractPercent}%)</span>
                              <span>• Resta: <strong className="text-orange-700">{formatCurrency(contract.remaining)}</strong></span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">{project.client_name || '-'}</td>
                        <td className="px-4 py-3">{getProjectStatusBadge(project.project_status)}</td>
                        <td className="px-4 py-3 text-sm">{getPaymentMethodBadge(project.payment_method)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatCurrency(project.expected_amount)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`font-semibold ${project.total_received > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                            {formatCurrency(project.total_received)}
                          </span>
                          {project.excess_amount > 0 && (
                            <span className="block text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded mt-0.5 border border-emerald-200">
                              + {formatCurrency(project.excess_amount)} (Excedente)
                            </span>
                          )}
                          {hasReceipts && (
                            <button
                              type="button"
                              onClick={() => toggleExpandReceipts(project.id)}
                              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium mt-1 transition-colors"
                            >
                              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              {isExpanded ? 'Ocultar pgtos' : `Ver ${project.receipts.length} pgto(s)`}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-orange-600">{formatCurrency(project.pending_amount)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {project.expected_date ? formatLocalDate(project.expected_date) : '-'}
                          {isLate && (
                            <span className="block mt-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-800 animate-pulse text-center w-fit">
                              Atrasado
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(project.status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {!isPaid && (
                              <button
                                onClick={() => handleOpenReceive(project)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
                                title="Dar baixa ou registrar recebimento parcial/total"
                              >
                                <Check className="w-3.5 h-3.5" />
                                Baixar
                              </button>
                            )}
                            {project.initial_balance_id ? (
                              <button
                                onClick={() => handleOpenInitialBalance(project)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg text-xs font-semibold transition-colors"
                                title="Abrir Detalhes do Saldo Inicial deste projeto"
                              >
                                <FileText className="w-3.5 h-3.5" />
                                Saldo
                              </button>
                            ) : (
                              <button
                                onClick={() => navigate(`/projetos/${project.project_id}/orcamento`)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-xs font-semibold transition-colors"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                Projeto
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(project.id)}
                                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                title="Excluir entrada prevista"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Accordion: Histórico de recebimentos individuais da parcela */}
                      {isExpanded && hasReceipts && (
                        <tr className="bg-blue-50/40 border-b border-blue-100">
                          <td colSpan="10" className="p-4">
                            <div className="bg-white rounded-xl p-3 border border-blue-200 shadow-2xs">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-xs font-bold text-blue-900 flex items-center gap-1.5">
                                  <span>💳 Histórico de Pagamentos Recebidos Desta Parcela</span>
                                  <span className="text-[11px] font-normal text-blue-600">({project.receipts.length} lançamento(s))</span>
                                </h4>
                                <span className="text-xs font-bold text-emerald-700">
                                  Total Recebido: {formatCurrency(project.total_received)}
                                </span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-xs divide-y divide-gray-100">
                                  <thead className="bg-gray-50 text-gray-500 font-semibold">
                                    <tr>
                                      <th className="px-3 py-1.5 text-left">Data do Recebimento</th>
                                      <th className="px-3 py-1.5 text-left">Forma de Pagto</th>
                                      <th className="px-3 py-1.5 text-left">Descrição do Lançamento</th>
                                      <th className="px-3 py-1.5 text-right">Valor Pago</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {project.receipts.map((rc, idx) => (
                                      <tr key={rc.id || idx} className="hover:bg-blue-50/30">
                                        <td className="px-3 py-2 font-medium text-gray-800">{formatLocalDate(rc.date)}</td>
                                        <td className="px-3 py-2">{getPaymentMethodBadge(rc.payment_method)}</td>
                                        <td className="px-3 py-2 text-gray-600">{rc.description || 'Recebimento de parcela'}</td>
                                        <td className="px-3 py-2 text-right font-bold text-green-700">{formatCurrency(rc.amount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr className="bg-gray-50 font-semibold text-sm">
                  <td colSpan="4" className="px-4 py-3 text-gray-900">Total Geral do Período</td>
                  <td className="px-4 py-3 text-gray-900">{formatCurrency(displayedTotals.total)}</td>
                  <td className="px-4 py-3 text-green-700">
                    {formatCurrency(displayedTotals.completed)}
                  </td>
                  <td className="px-4 py-3 text-orange-700">
                    {formatCurrency(displayedTotals.pending)}
                  </td>
                  <td colSpan="3" className="px-4 py-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Recebimento de Entrada Prevista (Baixa Rápida com valor e data flexíveis) */}
      {receiveModal.open && receiveModal.item && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-green-700 text-white flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <span>💵 Confirmar Recebimento de Entrada</span>
                </h3>
                <p className="text-xs text-emerald-100 mt-0.5">
                  Lance o valor efetivamente pago pelo cliente e atualize o livro caixa
                </p>
              </div>
              <button
                type="button"
                onClick={() => setReceiveModal({ open: false, item: null, receivedAmount: '', paymentDate: '', paymentMethod: 'DINHEIRO', observations: '' })}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleConfirmReceive} className="p-6 space-y-4">
              {/* Card de Resumo da Parcela */}
              <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-200 text-sm space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Projeto:</span>
                  <span className="font-bold text-gray-900">{receiveModal.item.project_code} - {receiveModal.item.project_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Cliente:</span>
                  <span className="font-medium text-gray-800">{receiveModal.item.client_name || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Parcela / Vencimento:</span>
                  <span className="font-medium text-gray-800">{receiveModal.item.parcel_label || 'Parcela'} • {formatLocalDate(receiveModal.item.expected_date)}</span>
                </div>
                <div className="pt-1.5 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-600">Restante Desta Parcela:</span>
                  <span className="text-base font-extrabold text-orange-600">{formatCurrency(receiveModal.item.pending_amount)}</span>
                </div>
              </div>

              {/* Campos de Entrada */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Valor Pago (R$) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500 font-bold text-sm">R$</span>
                    <input
                      type="text"
                      required
                      value={receiveModal.receivedAmount}
                      onChange={(e) => setReceiveModal(prev => ({ ...prev, receivedAmount: e.target.value }))}
                      placeholder="0,00"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base font-bold text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <span className="text-[11px] text-gray-500 mt-0.5 block">
                    Permite digitar valor parcial ou total
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Data do Pagamento *
                  </label>
                  <input
                    type="date"
                    required
                    value={receiveModal.paymentDate}
                    onChange={(e) => setReceiveModal(prev => ({ ...prev, paymentDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <span className="text-[11px] text-gray-500 mt-0.5 block">
                    Data em que o cliente pagou
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Forma de Pagamento *
                </label>
                <select
                  value={receiveModal.paymentMethod}
                  onChange={(e) => setReceiveModal(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                >
                  <option value="DINHEIRO">Dinheiro</option>
                  <option value="PIX">PIX</option>
                  <option value="CARTAO">Cartão de Crédito</option>
                  <option value="CARTAO_DEBITO">Cartão de Débito</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="TRANSFERENCIA">Transferência Bancária</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Observações (Opcional)
                </label>
                <input
                  type="text"
                  value={receiveModal.observations}
                  onChange={(e) => setReceiveModal(prev => ({ ...prev, observations: e.target.value }))}
                  placeholder="Ex: Pago em mãos, comprovante no WhatsApp..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Alerta explicativo em tempo real */}
              {(() => {
                const pending = parseFloat(receiveModal.item.pending_amount || 0);
                const typed = parseFloat(String(receiveModal.receivedAmount).replace(/\./g, '').replace(',', '.')) || 0;
                if (typed <= 0) return null;
                if (typed < pending) {
                  return (
                    <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong>Pagamento Parcial:</strong> R$ {typed.toFixed(2)} entrarão no livro caixa da empresa e restará <strong>R$ {(pending - typed).toFixed(2)}</strong> pendente nesta parcela.
                      </div>
                    </div>
                  );
                }
                if (typed === pending) {
                  return (
                    <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-xs text-emerald-800 flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong>Pagamento Integral:</strong> Esta parcela será 100% quitada e <strong>R$ {typed.toFixed(2)}</strong> serão creditados no caixa da empresa.
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-200 text-xs text-blue-800 flex items-start gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong>Pagamento com Excedente:</strong> O cliente está pagando <strong>R$ {(typed - pending).toFixed(2)}</strong> a mais que o previsto desta parcela. O valor integral de <strong>R$ {typed.toFixed(2)}</strong> entrará no caixa.
                    </div>
                  </div>
                );
              })()}

              <div className="pt-3 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setReceiveModal({ open: false, item: null, receivedAmount: '', paymentDate: '', paymentMethod: 'DINHEIRO', observations: '' })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={receiveMutation.isPending}
                  className="px-5 py-2 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  {receiveMutation.isPending ? 'Confirmando...' : 'Confirmar Recebimento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Integrado: Detalhes do Saldo Inicial */}
      {initialBalanceModal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <ProjectInitialBalances
              projectId={initialBalanceModal.projectId}
              projectName={initialBalanceModal.projectName}
              clientName={initialBalanceModal.clientName}
              onClose={() => setInitialBalanceModal({ open: false, projectId: null, projectName: '', clientName: '' })}
            />
          </div>
        </div>
      )}

      <ConfirmModal
        show={showConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir esta entrada prevista?"
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default FinanceForecastedIncomes;
